import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
  ManageAccounts as ManageAccountsIcon,
  Refresh as RefreshIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { FormNavigationProvider, NavTextField } from '../components/FormNavigation';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchUsers, refreshUserRoles, addUser, removeUser } from '../store/usersSlice';
import { showNotification } from '../store/notificationSlice';
import { checkAuth } from '../store/authSlice';
import { User, UserListItem } from '../types/users';

// ─── Create User Dialog ────────────────────────────────────────────────────────

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (user: UserListItem) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose, onCreated }) => {
  const allRoles = useAppSelector((state) => state.roles.roles);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [linkedEmployeeId, setLinkedEmployeeId] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  const handleClose = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setLinkedEmployeeId('');
    setSelectedRoleIds([]);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!username.trim()) { setError('Username is required'); return; }
    if (username.includes('@')) { setError('Username must not be an email address'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    if (!password.trim()) { setError('Temporary password is required'); return; }

    setSaving(true);
    setError(null);
    try {
      const createdUserResp = await apiService.createUser({
        username: username.trim(),
        email: email.trim(),
        temporaryPassword: password,
        ...(linkedEmployeeId.trim() && { linkedEmployeeId: linkedEmployeeId.trim() }),
      });

      const actualUsername = createdUserResp.data?.username || username.trim();

      // Assign selected roles (non-fatal if individual assignments fail)
      const assignedRoles: string[] = [];
      for (const roleId of selectedRoleIds) {
        try {
          await apiService.assignUserRole(actualUsername, roleId);
          assignedRoles.push(roleId);
        } catch {
          // Continue assigning remaining roles even if one fails
        }
      }

      onCreated({
        username: actualUsername,
        email: createdUserResp.data?.email || email.trim(),
        isUnitAdmin: false,
        roles: assignedRoles,
      });
      handleClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <NavTextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
            placeholder="e.g. john.doe"
          />
          <NavTextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g. john.doe@example.com"
          />
          <NavTextField
            label="Temporary Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Password policy: min 8 chars with 1 uppercase, 1 lowercase, 1 number, and 1 symbol (!@#$%^&*). Existing Cognito users keep their current password."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" size="small">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <NavTextField
            label="Linked Employee ID"
            value={linkedEmployeeId}
            onChange={(e) => setLinkedEmployeeId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
            helperText="Optional. UUID of the linked employee record."
          />
          {allRoles.length > 0 && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Initial Roles (optional)</InputLabel>
              <Select
                multiple
                value={selectedRoleIds}
                label="Initial Roles (optional)"
                onChange={(e) => setSelectedRoleIds(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => allRoles.find((r) => r.ID === id)?.Name ?? id)
                    .join(', ')
                }
              >
                {allRoles.map((role) => (
                  <MenuItem key={role.ID} value={role.ID}>
                    {role.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Create User
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
};

// ─── View User Dialog ──────────────────────────────────────────────────────────

interface ViewUserDialogProps {
  username: string | null;
  onClose: () => void;
}

const ViewUserDialog: React.FC<ViewUserDialogProps> = ({ username, onClose }) => {
  const open = Boolean(username);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !username) return;
    setUser(null);
    setError(null);
    setLoading(true);
    apiService
      .getUser(username)
      .then((r) => setUser(r.data))
      .catch((err) => setError(err?.message ?? 'Failed to load user'))
      .finally(() => setLoading(false));
  }, [open, username]);

  const cognitoStatus = (status?: string) => {
    if (!status) return null;
    const colors: Record<string, 'success' | 'warning' | 'default'> = {
      CONFIRMED: 'success',
      FORCE_CHANGE_PASSWORD: 'warning',
    };
    return <Chip label={status} size="small" color={colors[status] ?? 'default'} />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>User Details — {username}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {user && !loading && (
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Username</Typography>
                <Typography variant="body2" fontWeight={600}>{user.username}</Typography>
              </Box>
              {user.email && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{user.email}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                {cognitoStatus(user.status)}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Account Enabled</Typography>
                <Chip
                  label={user.enabled ? 'Yes' : 'No'}
                  size="small"
                  color={user.enabled ? 'success' : 'error'}
                />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Manage Roles Dialog ───────────────────────────────────────────────────────

interface ManageRolesDialogProps {
  username: string | null;
  isUnitAdmin: boolean;
  onClose: () => void;
}

const ManageRolesDialog: React.FC<ManageRolesDialogProps> = ({ username, isUnitAdmin, onClose }) => {
  const open = Boolean(username);
  const allRoles = useAppSelector((state) => state.roles.roles);
  const currentUsername = useAppSelector((state) => state.auth.userName);
  const dispatch = useAppDispatch();

  const [assignedRoleIds, setAssignedRoleIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !username) return;
    setError(null);
    setSelectedRoleToAdd('');
    setLoading(true);
    apiService.getUserRoles(username)
      .then((res) => setAssignedRoleIds(res.data ?? []))
      .catch((err) => setError(err?.message ?? 'Failed to load roles'))
      .finally(() => setLoading(false));
  }, [open, username]);

  const roleLabel = (roleId: string): string => {
    const found = allRoles.find((r) => r.ID === roleId);
    return found ? found.Name : roleId.replace(/^ROLE#/, '');
  };

  const isSystemAdminRole = (roleId: string) => roleId === 'ROLE#system-admin';

  const handleRemoveRole = async (roleId: string) => {
    if (!username) return;
    setSaving(true);
    try {
      await apiService.removeUserRole(username, roleId);
      setAssignedRoleIds((prev) => prev.filter((id) => id !== roleId));
      dispatch(refreshUserRoles(username));
      if (username === currentUsername) dispatch(checkAuth(true));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRole = async () => {
    if (!username || !selectedRoleToAdd) return;
    setSaving(true);
    try {
      await apiService.assignUserRole(username, selectedRoleToAdd);
      setAssignedRoleIds((prev) => [...prev, selectedRoleToAdd]);
      setSelectedRoleToAdd('');
      dispatch(refreshUserRoles(username));
      if (username === currentUsername) dispatch(checkAuth(true));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to assign role');
    } finally {
      setSaving(false);
    }
  };

  // System Admin is only non-assignable for the unit admin (it's inherent to them)
  const unassignedRoles = allRoles.filter(
    (r) => !assignedRoleIds.includes(r.ID) && !(isUnitAdmin && isSystemAdminRole(r.ID)),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Roles — {username}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && (
          <>
            {/* Current roles */}
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
              Assigned Roles
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, minHeight: 32 }}>
              {assignedRoleIds.length === 0 && (
                <Typography variant="body2" color="text.disabled">No roles assigned.</Typography>
              )}
              {assignedRoleIds.map((roleId) => {
                const systemAdminLocked = isUnitAdmin && isSystemAdminRole(roleId);
                return (
                  <Tooltip
                    key={roleId}
                    title={systemAdminLocked ? 'System Admin is inherent to the unit admin and cannot be removed' : ''}
                  >
                    <Chip
                      label={roleLabel(roleId)}
                      size="small"
                      color={systemAdminLocked ? 'default' : 'primary'}
                      onDelete={systemAdminLocked ? undefined : () => handleRemoveRole(roleId)}
                      disabled={saving}
                    />
                  </Tooltip>
                );
              })}
            </Box>

            {/* Add role section */}
            {unassignedRoles.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Add Role
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Select role</InputLabel>
                    <Select
                      value={selectedRoleToAdd}
                      label="Select role"
                      onChange={(e) => setSelectedRoleToAdd(e.target.value)}
                    >
                      {unassignedRoles.map((role) => (
                        <MenuItem key={role.ID} value={role.ID}>
                          {role.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleAddRole}
                    disabled={!selectedRoleToAdd || saving}
                    sx={{ mt: 0.5 }}
                  >
                    Add
                  </Button>
                </Box>
              </>
            )}

            {unassignedRoles.length === 0 && assignedRoleIds.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                All available roles are already assigned.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delete User Dialog ────────────────────────────────────────────────────────

interface DeleteUserDialogProps {
  username: string | null;
  onClose: () => void;
  onDeleted: (username: string) => void;
}

const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({ username, onClose, onDeleted }) => {
  const open = Boolean(username);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!username) return;
    setDeleting(true);
    setError(null);
    try {
      await apiService.deleteUser(username);
      onDeleted(username);
      handleClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete User</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>
          Are you sure you want to delete <strong>{username}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will permanently remove the user from Cognito and all their unit assignments.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={deleting}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
          {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const Users: React.FC = () => {
  const { selectedUnit } = useAuth();
  const dispatch = useAppDispatch();

  const users = useAppSelector((state) => state.users.users);
  const userRoles = useAppSelector((state) => state.users.userRoles);
  const isLoading = useAppSelector((state) => state.users.isLoading);
  const error = useAppSelector((state) => state.users.error);
  const allRoles = useAppSelector((state) => state.roles.roles);

  const [createOpen, setCreateOpen] = useState(false);
  const [viewUsername, setViewUsername] = useState<string | null>(null);
  const [rolesUsername, setRolesUsername] = useState<string | null>(null);
  const [deleteUsername, setDeleteUsername] = useState<string | null>(null);


  const handleCreated = (user: UserListItem) => {
    dispatch(addUser(user));
    dispatch(showNotification({ message: `User "${user.username}" created successfully`, severity: 'success' }));
  };

  const handleDeleted = (username: string) => {
    dispatch(removeUser(username));
    dispatch(showNotification({ message: `User "${username}" deleted successfully`, severity: 'success' }));
  };

  const roleLabel = (roleId: string) => {
    const found = allRoles.find((r) => r.ID === roleId);
    return found ? found.Name : roleId.replace(/^ROLE#/, '');
  };

  const rolesUser = rolesUsername ? users.find((u) => u.username === rolesUsername) : null;

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              User Management
            </Typography>
            {selectedUnit && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedUnit.tenant} › {selectedUnit.solution} › {selectedUnit.unit}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={() => dispatch(fetchUsers())} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {selectedUnit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Add User
              </Button>
            )}
          </Box>
        </Box>

        {/* No unit selected */}
        {!selectedUnit && (
          <Alert severity="warning">
            No unit is selected. Please select a unit to manage users.
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        {selectedUnit && (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No users found for this unit.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.username} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="View details">
                            <IconButton size="small" onClick={() => setViewUsername(user.username)}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2" fontWeight={500}>
                            {user.username}
                          </Typography>
                          {user.isUnitAdmin && (
                            <Chip label="Unit Admin" size="small" color="secondary" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(userRoles[user.username] ?? []).length === 0 ? (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          ) : (
                            (userRoles[user.username] ?? []).map((roleId) => (
                              <Chip
                                key={roleId}
                                label={roleLabel(roleId)}
                                size="small"
                                variant="outlined"
                              />
                            ))
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Manage roles">
                          <IconButton
                            size="small"
                            onClick={() => setRolesUsername(user.username)}
                          >
                            <ManageAccountsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isUnitAdmin ? 'Unit admin cannot be deleted' : 'Delete user'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteUsername(user.username)}
                              disabled={user.isUnitAdmin}
                              sx={{ ml: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Dialogs */}
      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <ViewUserDialog
        username={viewUsername}
        onClose={() => setViewUsername(null)}
      />

      <ManageRolesDialog
        username={rolesUsername}
        isUnitAdmin={rolesUser?.isUnitAdmin ?? false}
        onClose={() => setRolesUsername(null)}
      />

      <DeleteUserDialog
        username={deleteUsername}
        onClose={() => setDeleteUsername(null)}
        onDeleted={handleDeleted}
      />

    </DashboardLayout>
  );
};

export default Users;
